import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import { reportError } from 'src/easyclaim.app.libs/reportError';
import styled from 'styled-components';

import { EasyClaimDocumentType } from '../easyclaim.api.client.typescript/src/index';
import { PlaceholderFrontLeftV2, PlaceholderRearRightV2 } from '../easyclaim.app.assets/index';
import {
	DetailContainer,
	DetailsContainer,
	SelectImage,
	StyledInfoIcon,
	ExampleModalImage,
} from '../easyclaim.app.components/index';
import { ImageSelectionService, Image } from '../easyclaim.app.services';

import { ScreenComponent } from './ScreenComponent';

interface VehicleImagesState {}

function determineNewStateAfterLoadingFrontLeftImage(
	currentAppState: AppState,
	newFrontLeftImage: Image | undefined
): AppState {
	const isFrontLeftImageLoading = ImageSelectionService.isImageLoading(newFrontLeftImage);
	const isRearRightImageLoading = ImageSelectionService.isImageLoading(
		currentAppState.vehicleImageRearRight
	);

	const isImageLoading = !!isFrontLeftImageLoading || !!isRearRightImageLoading;

	const newState = Object.assign({}, currentAppState, {
		vehicleImageFrontLeft: newFrontLeftImage,
		isImageLoading: isImageLoading,
	});

	return newState;
}

function determineNewStateAfterLoadingRearRightImage(
	currentAppState: AppState,
	newRearRightImage: Image | undefined
): AppState {
	const isFrontLeftImageLoading = ImageSelectionService.isImageLoading(
		currentAppState.vehicleImageFrontLeft
	);
	const isRearRightImageLoading = ImageSelectionService.isImageLoading(newRearRightImage);

	const isImageLoading = !!isFrontLeftImageLoading || !!isRearRightImageLoading;

	const newState = Object.assign({}, currentAppState, {
		vehicleImageRearRight: newRearRightImage,
		isImageLoading: isImageLoading,
	});

	return newState;
}

export class VehicleImages extends ScreenComponent<AppState, VehicleImagesState> {
	constructor(props: AppState) {
		super(props);

		this.state = {};

		this.showVehicleImagesExampleModal = this.showVehicleImagesExampleModal.bind(this);
	}

	async showVehicleImagesExampleModal() {
		const images: ExampleModalImage[] = [
			{
				src: `${process.env.PUBLIC_URL}/images/example-diagonal-1.jpg`,
			},
			{
				src: `${process.env.PUBLIC_URL}/images/example-diagonal-2.jpg`,
			},
		];

		await this.props.showExampleModal(
			images,
			'vehicleImagesExampleModal.headline.first',
			'vehicleImagesExampleModal.button.close',
			'example-diagonals'
		);
	}

	public render() {
		return (
			<div className="main-container">
				<h2 className="vehicle-images-title">
					<FormattedMessage id="vehicleImages.overviewPhotos" />
				</h2>
				<DetailsContainer>
					<DetailContainer>
						<Paragraph>
							<FormattedMessage id="vehicleImages.details.frontleft" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'VehicleImages.asyncAction' });
									});
								}}
							/>
						</Paragraph>
						<SelectImage
							componentId="vehicleFrontLeft"
							fileType={EasyClaimDocumentType.FrontLeftPicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageFrontLeft}
							title="vehicleImages.leftFrontOverview"
							placeholder={<PlaceholderFrontLeftV2 className="photo-placeholder" />}
							marginBottom="1rem"
							onChange={(image) => {
								const newState = determineNewStateAfterLoadingFrontLeftImage(this.props, image);

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, { context: 'VehicleImages.showVehicleImagesExampleModal' });
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
						<Paragraph>
							<FormattedMessage id="vehicleImages.details.rearright" />{' '}
							<StyledInfoIcon
								onClick={() => {
									void this.showVehicleImagesExampleModal().catch((error: unknown) => {
										reportError(error, { context: 'VehicleImages.asyncAction' });
									});
								}}
							/>
						</Paragraph>
						<SelectImage
							componentId="vehicleRearRight"
							fileType={EasyClaimDocumentType.RearRightPicture}
							caseId={this.props.easyClaimCase?.caseId}
							easyClaimService={this.props.easyClaimService}
							currentImage={this.props.vehicleImageRearRight}
							title="vehicleImages.rightRearOverview"
							placeholder={<PlaceholderRearRightV2 className="photo-placeholder" />}
							marginBottom="1rem"
							onChange={(image) => {
								const newState = determineNewStateAfterLoadingRearRightImage(this.props, image);

								void this.props.setAppState(newState);
							}}
							onShowExample={() => {
								void this.showVehicleImagesExampleModal().catch((error: unknown) => {
									reportError(error, { context: 'VehicleImages.showVehicleImagesExampleModal' });
								});
							}}
							showUserFeedbackInfo={true}
							configuration={this.props.easyClaimCase!._configuration!}
							inputError={this.props.inputError}
							canOverlayCamera={this.props.easyClaimCase?._configuration?.canShowOverlayOnCamera}
						/>
					</DetailContainer>
				</DetailsContainer>
			</div>
		);
	}
}

const Paragraph = styled.p`
	margin-bottom: 1rem;
	hyphens: auto;
	user-select: none;
	cursor: default;
`;
